import React, { memo } from 'react'
import { useDispatch } from 'react-redux'
import capitalize from 'lodash/capitalize'
import moment from 'moment-timezone'
import { Dropdown, DropdownIconType, IDropdownOption } from 'components/forms'
import { TableRow, TableCell } from 'transfix-ui'
import { REGION_ABBREVIATIONS } from 'constants/countriesStates'
import { FORMATS } from 'constants/datetime'
import { ICostControlConfig, CONTROL_OPTIONS, RULE_TYPES } from '../types/costControlConfig'
import {
  openAddCostControlConfigModal,
  openDeleteCostControlConfigModal,
} from '../utils/actions/costControlConfig'

const formatRegion = (regionAbbr: REGION_ABBREVIATIONS | string): string => {
  switch (regionAbbr) {
    case REGION_ABBREVIATIONS.C:
      return 'Coastal'
    case REGION_ABBREVIATIONS.S:
      return 'South'
    case REGION_ABBREVIATIONS.W:
      return 'West'
    case REGION_ABBREVIATIONS.N:
      return 'Northeast'
    case REGION_ABBREVIATIONS.MW:
      return 'Midwest'
    case REGION_ABBREVIATIONS.SE:
      return 'Southeast'
    default:
      return ''
  }
}

const formatRuleType = (ruleType: RULE_TYPES): string => {
  switch (ruleType) {
    case RULE_TYPES.baseline:
      return 'Baseline'
    case RULE_TYPES.lane:
      return 'Lane (PU + DEL)'
    case RULE_TYPES.outbound:
      return 'Outbound (PU)'
    default:
      return ''
  }
}

const formatCostControlOption = (controlOption: CONTROL_OPTIONS): string => {
  switch (controlOption) {
    case CONTROL_OPTIONS.quoting:
      return 'Spot Quoting'
    case CONTROL_OPTIONS.covering:
      return 'CM Target'
    case CONTROL_OPTIONS.sbr:
      return 'SBR'
    default:
      return ''
  }
}

const formatRuleValue = (
  ruleType: RULE_TYPES,
  pickupRegion: REGION_ABBREVIATIONS | string,
  deliveryRegion: REGION_ABBREVIATIONS | string
): string => {
  switch (ruleType) {
    case RULE_TYPES.lane:
      return `${formatRegion(pickupRegion)} to ${formatRegion(deliveryRegion)}`
    case RULE_TYPES.outbound:
      return `${formatRegion(pickupRegion)}`
    default:
      return ''
  }
}

const formatDateRange = (rangeStart?: string, rangeEnd?: string): string => {
  if (!rangeStart) return ''
  const start = moment(rangeStart).format(FORMATS.dateFormatShort)
  const end = moment(rangeEnd).format(FORMATS.dateFormatShort)
  if (start === end) {
    return start
  }
  return `${start} to ${end}`
}

interface ICostControlConfigsTableRowProps {
  costControlConfig: ICostControlConfig
  readOnly: boolean
}

export const CostControlConfigsTableRow = memo<ICostControlConfigsTableRowProps>(
  ({ costControlConfig, readOnly }) => {
    const dispatch = useDispatch()
    const {
      controlOption,
      costStrategy,
      deliveryRegion,
      id,
      isDrop,
      pickupRegion,
      rangeEnd,
      rangeStart,
      ruleType,
    } = costControlConfig
    const ruleTypeFormatted = formatRuleType(ruleType)
    const ruleValue = formatRuleValue(ruleType, pickupRegion, deliveryRegion)
    const dateRange = formatDateRange(rangeStart, rangeEnd)

    const options: IDropdownOption[] = [
      {
        label: 'Edit Rule',
        action: () => dispatch(openAddCostControlConfigModal(costControlConfig)),
      },
    ]
    if (ruleType !== RULE_TYPES.baseline) {
      options.push({
        label: 'Delete Rule',
        action: () => dispatch(openDeleteCostControlConfigModal(costControlConfig)),
      })
    }

    return (
      <TableRow dataTest={`cost-control-config-${id}`}>
        <TableCell dataTest={`cost-control-config-${id}-control`}>
          {formatCostControlOption(controlOption)}
        </TableCell>
        <TableCell dataTest={`cost-control-config-${id}-rule-type`}>{ruleTypeFormatted}</TableCell>
        <TableCell dataTest={`cost-control-config-${id}-rule-value`}>{ruleValue}</TableCell>
        <TableCell dataTest={`cost-control-config-${id}-cost-strategy`}>
          {costStrategy.replace('_', '')}
        </TableCell>
        <TableCell dataTest={`cost-control-config-${id}-date-range`}>{dateRange}</TableCell>
        <TableCell dataTest={`cost-control-config-${id}-drop-status`}>
          {capitalize(isDrop?.toString())}
        </TableCell>
        {!readOnly && (
          <TableCell className='tr' dataTest={`cost-control-config-${id}-actions`}>
            <Dropdown
              options={options}
              icon={DropdownIconType.ellipsis}
              dataTest={`cost-control-config-${id}-dropdown`}
            />
          </TableCell>
        )}
      </TableRow>
    )
  }
)
